import { Sparkles } from 'lucide-react'
import { useMemo, useState } from 'react'
import { toast } from 'sonner'

import { type AircallNumber } from '@/components/Aircall/types'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Card, CardContent } from '@/components/ui/card'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { Textarea } from '@/components/ui/textarea'
import { parseError } from '@/lib/errors'
import {
  type SalesLeadActivity,
  useDiscardSmsDraft,
  useSendSmsDraft,
  useUpdateSmsDraft,
} from '@/models/salesLead'

const SMS_SEGMENT_LEN = 160

function segmentsOf(text: string): number {
  return text.length === 0 ? 0 : Math.ceil(text.length / SMS_SEGMENT_LEN)
}

function CharCount({ text }: { text: string }) {
  const segments = segmentsOf(text)
  return (
    <span className="text-muted-foreground text-xs">
      {text.length} chars
      {segments > 0 && (
        <>
          {' · '}
          {segments} segment{segments === 1 ? '' : 's'}
        </>
      )}
    </span>
  )
}

function optionLabel(option: AircallNumber): string {
  return option.label && option.label !== option.value ? `${option.label} · ${option.value}` : option.value
}

interface SmsDraftActivityCardProps {
  leadId: string
  activity: SalesLeadActivity
  numbers: AircallNumber[]
}

export function SmsDraftActivityCard({ leadId, activity, numbers }: SmsDraftActivityCardProps) {
  const persistedBody = activity.draftBody ?? ''

  // Recipient options: the lead's contact numbers, plus the draft's resolved
  // default (e.g. a reply's inbound sender) if it isn't already a contact number.
  const options = useMemo(() => {
    const opts = [...numbers]
    const preset = activity.draftSmsTo
    if (preset && !opts.some((n) => n.value === preset)) {
      opts.unshift({ label: preset, value: preset })
    }
    return opts
  }, [numbers, activity.draftSmsTo])

  const [to, setTo] = useState(activity.draftSmsTo ?? (options.length > 0 ? options[0].value : ''))
  const [editing, setEditing] = useState(false)
  const [body, setBody] = useState(persistedBody)

  const sendDraft = useSendSmsDraft()
  const updateDraft = useUpdateSmsDraft()
  const discardDraft = useDiscardSmsDraft()
  const busy = sendDraft.isPending || updateDraft.isPending || discardDraft.isPending

  const canSave = body.trim().length > 0
  const canSend = !!to && persistedBody.trim().length > 0

  const startEditing = () => {
    setBody(persistedBody)
    setEditing(true)
  }

  const handleCancel = () => {
    setBody(persistedBody)
    setEditing(false)
  }

  const handleSave = () => {
    if (!canSave) return
    updateDraft.mutate(
      { leadId, activityId: activity.id, content: body },
      {
        onSuccess: () => setEditing(false),
        onError: (err) => toast.error(parseError(err)),
      }
    )
  }

  const handleSend = () => {
    if (!canSend) return
    sendDraft.mutate(
      { leadId, activityId: activity.id, to, content: persistedBody },
      {
        onSuccess: () => toast.success('SMS sent'),
        onError: (err) => toast.error(parseError(err)),
      }
    )
  }

  const handleDiscard = () => {
    discardDraft.mutate({ leadId, activityId: activity.id })
  }

  if (editing) {
    return (
      <div className="flex flex-col gap-2">
        <Textarea
          value={body}
          onChange={(e) => setBody(e.target.value)}
          disabled={busy}
          placeholder="Edit message…"
          aria-label="SMS draft message"
        />
        <div className="flex items-center gap-2">
          <CharCount text={body} />
          <div className="ml-auto flex gap-2">
            <Button variant="ghost" size="sm" onClick={handleCancel} disabled={busy}>
              Cancel
            </Button>
            <Button size="sm" onClick={handleSave} disabled={busy || !canSave}>
              Save
            </Button>
          </div>
        </div>
      </div>
    )
  }

  return (
    <Card className="border-orange-200 bg-orange-50">
      <CardContent>
        <div className="flex items-center gap-2">
          <Badge
            variant="outline"
            className="gap-1 border-purple-300 bg-purple-50 px-2 py-0.5 text-purple-700"
          >
            <Sparkles className="size-3" />
            AI SMS Draft
          </Badge>
          <span className="text-muted-foreground text-sm">
            {new Date(activity.createdAt).toLocaleString(undefined, {
              month: 'long',
              day: 'numeric',
              year: 'numeric',
              hour: 'numeric',
              minute: '2-digit',
            })}
          </span>
        </div>
        {options.length === 0 ? (
          <p className="mt-2 text-xs text-red-600">No phone number on file — add one to send.</p>
        ) : options.length === 1 ? (
          <div className="text-muted-foreground mt-2 text-sm">
            To: <span className="font-medium">{options[0].value}</span>
          </div>
        ) : (
          <div className="mt-2 flex items-center gap-2">
            <span className="text-muted-foreground text-sm">To:</span>
            <Select value={to} onValueChange={setTo} disabled={busy}>
              <SelectTrigger
                className="h-7! w-fit gap-1 border-orange-200 bg-orange-50 px-2 py-0 text-sm"
                aria-label="Recipient number"
              >
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {options.map((option) => (
                  <SelectItem key={option.value} value={option.value}>
                    {optionLabel(option)}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        )}
        <p className="mt-2 text-sm whitespace-pre-wrap">{persistedBody}</p>
        <div className="mt-1">
          <CharCount text={persistedBody} />
        </div>
        <div className="mt-3 flex items-center gap-2">
          <div className="ml-auto flex gap-2">
            <Button variant="ghost" size="sm" onClick={handleDiscard} disabled={busy}>
              Discard
            </Button>
            <Button variant="outline" size="sm" onClick={startEditing} disabled={busy}>
              Edit
            </Button>
            <Button size="sm" onClick={handleSend} disabled={busy || !canSend}>
              Send
            </Button>
          </div>
        </div>
      </CardContent>
    </Card>
  )
}
